// Combined Budget + Cashflow workbook.
//
// Three worksheets in one file so the registrar can hand a single
// document to the board / advisors:
//   1. Budget Projection — annual figures, 3-year projection
//   2. Cashflow — monthly grid (actual + projected)
//   3. Reconciliation — per-line annual budget alongside the sum of
//      cashflow window months, so the link between the two is explicit.
//
// Reuses the existing single-sheet builders via addBudgetWorksheet /
// addCashflowWorksheet so styling stays consistent.

import type { Workbook, Cell, Worksheet } from 'exceljs';
import { yearLabel } from './format';
import { addBudgetWorksheet } from './xlsxExport';
import { addCashflowWorksheet, type CashflowXlsxInput } from './cashflowXlsxExport';
import type { DerivedBudget } from './totals';
import type { ProjectedLine } from './projection';

const BRAND = 'FF85754D';
const BLUE = 'FF005487';
const BLUE_LIGHT = 'FFE5EEF4';
const TEXT = 'FF111827';
const TEXT_SOFT = 'FF6B7280';
const BORDER = 'FFE5E0D5';

const ACCOUNTING = '#,##0;(#,##0);"-";@';
const round = (n: number) => Math.round(n);

export type CombinedXlsxInput = CashflowXlsxInput & {
  derived: DerivedBudget;
  baseYear: number;
};

/** NZ FY convention: October–September. FY N = Oct (N−1) → Sep N.
 *  So 2025-10 belongs to FY2026, 2026-09 to FY2026, 2026-10 to FY2027. */
function fyForYm(ym: string): number {
  const parts = ym.split('-').map((s) => parseInt(s, 10));
  const y = parts[0] ?? 0;
  const m = parts[1] ?? 0;
  return m >= 10 ? y + 1 : y;
}

/** Sum a single line's monthly values, but only for months that fall in
 *  the Current FY (the budget's baseYear). Comparing this against the
 *  budget's "Current" column gives an apples-to-apples variance. */
function sumLineInCurrentFy(
  lineId: string,
  direction: 'income' | 'expense',
  projection: CashflowXlsxInput['projection'],
  baseYear: number,
): number {
  let total = 0;
  for (const m of projection.months) {
    if (fyForYm(m.ym) !== baseYear) continue;
    const byLine = direction === 'income' ? m.incomeByLine : m.expenseByLine;
    total += byLine[lineId] ?? 0;
  }
  return total;
}

/** How many of the 12 Current-FY months are present in the cashflow window. */
function currentFyCoverage(projection: CashflowXlsxInput['projection'], baseYear: number): number {
  return projection.months.filter((m) => fyForYm(m.ym) === baseYear).length;
}

function addReconciliationWorksheet(wb: Workbook, input: CombinedXlsxInput): Worksheet {
  const { derived, baseYear, projection } = input;
  const months = projection.months;
  const firstYm = months[0]?.ym ?? '';
  const lastYm = months[months.length - 1]?.ym ?? '';
  const monthCount = months.length;
  const fyCoverage = currentFyCoverage(projection, baseYear);
  const fyComplete = fyCoverage === 12;

  const ws = wb.addWorksheet('Reconciliation', {
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
    views: [{ state: 'frozen', ySplit: 5 }],
  });

  ws.getColumn(1).width = 8;
  ws.getColumn(2).width = 40;
  ws.getColumn(3).width = 14;
  ws.getColumn(4).width = 14;
  ws.getColumn(5).width = 14;
  ws.getColumn(6).width = 14;
  ws.getColumn(7).width = 16;
  ws.getColumn(8).width = 16;

  // Title
  const title = ws.addRow(['Budget ⇄ Cashflow Reconciliation']);
  title.font = { bold: true, color: { argb: BRAND }, size: 14 };
  ws.mergeCells(1, 1, 1, 8);

  const meta = ws.addRow([
    `Generated ${new Date().toLocaleString()} · Cashflow window ${firstYm} → ${lastYm} (${monthCount} months) · Current FY (${yearLabel(baseYear, 0)}) coverage ${fyCoverage}/12 months`,
  ]);
  meta.font = { color: { argb: TEXT_SOFT }, italic: true, size: 10 };
  ws.mergeCells(2, 1, 2, 8);

  const note = ws.addRow([
    fyComplete
      ? "Variance compares each line's Current FY budget against the sum of cashflow months that fall within the same FY (Oct–Sep). Apples-to-apples — green = favourable, red = unfavourable (over budget for expenses, under for income)."
      : `Only ${fyCoverage} of 12 Current FY months are present in the cashflow window — the variance reflects partial coverage and should be read as "tracking so far", not annual delta. Green = favourable, red = unfavourable.`,
  ]);
  note.font = { color: { argb: TEXT_SOFT }, italic: true, size: 9 };
  note.alignment = { wrapText: true };
  note.height = 36;
  ws.mergeCells(3, 1, 3, 8);

  ws.addRow([]);

  // Header row
  const header = ws.addRow([
    'Code',
    'Line',
    `${yearLabel(baseYear, 0)} (Current)`,
    `${yearLabel(baseYear, 1)}`,
    `${yearLabel(baseYear, 2)}`,
    `${yearLabel(baseYear, 3)}`,
    `Cashflow ${yearLabel(baseYear, 0)}${fyComplete ? '' : ` (${fyCoverage}/12)`}`,
    'Variance vs Current',
  ]);
  header.font = { bold: true, color: { argb: BRAND }, size: 11 };
  header.height = 24;
  header.eachCell((cell, col) => {
    cell.alignment = {
      horizontal: col >= 3 ? 'right' : 'left',
      vertical: 'middle',
      wrapText: true,
    };
    cell.border = { bottom: { style: 'medium', color: { argb: BORDER } } };
  });

  // Number-format for numeric columns (3..8)
  for (let c = 3; c <= 8; c += 1) {
    ws.getColumn(c).numFmt = ACCOUNTING;
    ws.getColumn(c).alignment = { horizontal: 'right' };
  }

  // Sign-aware variance colour. Income: positive variance = favourable
  // (more than budgeted) → green. Expense: positive variance = unfavourable
  // (overspent) → red. Net surplus follows the income rule.
  const FAVOURABLE = 'FF0F7B0F'; // green
  const UNFAVOURABLE = 'FFB91C1C'; // red
  const colourVariance = (
    cell: Cell,
    variance: number,
    direction: 'income' | 'expense' | 'net',
  ) => {
    if (Math.abs(variance) < 0.5) return;
    const positiveIsFavourable = direction !== 'expense';
    const favourable = positiveIsFavourable ? variance > 0 : variance < 0;
    cell.font = { color: { argb: favourable ? FAVOURABLE : UNFAVOURABLE }, size: 10 };
  };

  function addSection(
    label: string,
    lines: ProjectedLine[],
    direction: 'income' | 'expense',
  ): { current: number; y1: number; y2: number; y3: number; cashflow: number } {
    const sec = ws.addRow(['', label.toUpperCase(), '', '', '', '', '', '']);
    sec.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
    sec.height = 22;
    sec.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLUE } };
    });

    const totals = { current: 0, y1: 0, y2: 0, y3: 0, cashflow: 0 };

    for (const line of lines) {
      const cf = sumLineInCurrentFy(line.id, direction, projection, baseYear);
      const variance = cf - line.current;
      const r = ws.addRow([
        line.code,
        line.name,
        round(line.current),
        round(line.y1),
        round(line.y2),
        round(line.y3),
        round(cf),
        round(variance),
      ]);
      r.font = { color: { argb: TEXT }, size: 10 };
      colourVariance(r.getCell(8), variance, direction);

      totals.current += line.current;
      totals.y1 += line.y1;
      totals.y2 += line.y2;
      totals.y3 += line.y3;
      totals.cashflow += cf;
    }

    const totalRow = ws.addRow([
      '',
      `Total ${label}`,
      round(totals.current),
      round(totals.y1),
      round(totals.y2),
      round(totals.y3),
      round(totals.cashflow),
      round(totals.cashflow - totals.current),
    ]);
    totalRow.font = { bold: true, color: { argb: TEXT }, size: 11 };
    totalRow.height = 22;
    totalRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLUE_LIGHT } };
      cell.border = {
        top: { style: 'medium', color: { argb: TEXT } },
        bottom: { style: 'thin', color: { argb: TEXT } },
      };
    });
    colourVariance(totalRow.getCell(8), totals.cashflow - totals.current, direction);
    ws.addRow([]);
    return totals;
  }

  const incomeTotals = addSection('Income', derived.incomeProjected, 'income');
  const expenseTotals = addSection('Expenses', derived.expensesProjected, 'expense');

  // Net summary at bottom
  const netRow = ws.addRow([
    '',
    'Net (Income − Expenses)',
    round(incomeTotals.current - expenseTotals.current),
    round(incomeTotals.y1 - expenseTotals.y1),
    round(incomeTotals.y2 - expenseTotals.y2),
    round(incomeTotals.y3 - expenseTotals.y3),
    round(incomeTotals.cashflow - expenseTotals.cashflow),
    round(
      incomeTotals.cashflow -
        expenseTotals.cashflow -
        (incomeTotals.current - expenseTotals.current),
    ),
  ]);
  netRow.font = { bold: true, color: { argb: BLUE }, size: 12 };
  netRow.height = 24;
  netRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BLUE_LIGHT } };
    cell.border = {
      top: { style: 'medium', color: { argb: BLUE } },
      bottom: { style: 'medium', color: { argb: BLUE } },
    };
  });
  const netVariance =
    incomeTotals.cashflow - expenseTotals.cashflow - (incomeTotals.current - expenseTotals.current);
  colourVariance(netRow.getCell(8), netVariance, 'net');

  return ws;
}

export async function buildCombinedXlsx(input: CombinedXlsxInput): Promise<ArrayBuffer> {
  const { Workbook: WorkbookImpl } = await import('exceljs');
  const wb = new WorkbookImpl() as Workbook;
  wb.creator = 'CCAANZ Diocesan Budget Tool';
  wb.lastModifiedBy = 'CCAANZ Diocesan Budget Tool';
  wb.created = new Date();
  wb.modified = new Date();

  addBudgetWorksheet(wb, input.derived, input.baseYear);
  addCashflowWorksheet(wb, input);
  addReconciliationWorksheet(wb, input);

  return wb.xlsx.writeBuffer() as Promise<ArrayBuffer>;
}

export function combinedXlsxFilename(baseYear: number): string {
  const date = new Date().toISOString().slice(0, 10);
  return `CCAANZ_Budget_and_Cashflow_${yearLabel(baseYear, 1)}-${yearLabel(baseYear, 3)}_${date}.xlsx`;
}

export async function downloadCombinedXlsx(input: CombinedXlsxInput): Promise<void> {
  const buffer = await buildCombinedXlsx(input);
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = combinedXlsxFilename(input.baseYear);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
